import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const useStyles = makeStyles(() => ({
  container: {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  tableRowData: {
    borderBottom: '2px solid #EBEBEB',
    backgroundColor: 'transparent',
    fontFamily: 'Rubik',
    width: '100%',
    fontSize: '15px',
    padding: '4px',
    paddingRight: '6px',
    fontWeight: 600,
    color: '#08231B',
    letterSpacing: '0.75px',
    '&:last-child': { borderBottom: 'none' },
  },
}));

const headerSx = { fontSize: { xs: '10px', md: '13px', lg: '14.67px', xl: '21px' }, fontFamily: 'Rubik', fontWeight: 800, color: '#08231B' };
const cellSx   = { fontSize: { xs: '10px', md: '11px', lg: '13px', xl: '19px' }, fontWeight: 400, fontFamily: 'Rubik', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const btnSx    = { textTransform: 'none', fontFamily: 'Rubik', fontWeight: 600, fontSize: { xs: '10px', sm: '10px', md: '11px', lg: '12px', xl: '16px' }, height: { xs: '18px', sm: '18px', md: '20px', lg: '22px', xl: '36px' }, borderRadius: '5px', whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: { xs: 0.4, lg: 0.5 } } };

// Buttons on the xs/sm card layout (below the md breakpoint where the grid row takes over).
const mobileBtnSx = {
  textTransform: 'none',
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: { xs: '9px', sm: '9.5px' },
  height: { xs: '20px', sm: '22px' },
  minWidth: 0,
  lineHeight: 1,
  px: 0.7,
  py: 0,
  borderColor: '#08231B',
  '& .MuiButton-startIcon': { mr: 0.3 },
};

// fr-based tracks so columns shrink/grow fluidly with the available width instead of
// overflowing or overlapping between the md/lg/xl breakpoints; the actions column keeps a
// fixed px track so the buttons never get squeezed.
const gridTemplateColumns = {
  md: '1.7fr 1.3fr 1fr 1.1fr 1.3fr 150px',
  lg: '1.7fr 1.3fr 1fr 1.1fr 1.3fr 170px',
  xl: '1.7fr 1.3fr 1fr 1.1fr 1.3fr 230px',
};
const rowGridSx = { display: { xs: 'none', md: 'grid' }, gridTemplateColumns, alignItems: 'center', columnGap: { md: 1, xl: 2 }, minWidth: '700px' };

// created_at from the API is a unix timestamp in seconds.
const formatDate = (unixSeconds) => {
  if (!unixSeconds) return '—';
  const d = new Date(unixSeconds * 1000);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// expected_delivery from the API is a unix timestamp in seconds.
const formatDelivery = (unixSeconds) => {
  if (!unixSeconds) return '—';
  const d = new Date(unixSeconds * 1000);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

const QuickTurnTable = ({ requests = [], isLoading, onAccept, onReject, actionLoading, hasNextPage, onLoadMore }) => {
  const classes = useStyles();

  return (
    <Box mt={3} className='flex col'>
      <Box sx={{ overflowY: 'scroll', overflowX: 'auto', height: '60vh' }} className={classes.container}>

        {/* ── Desktop header ── */}
        <Box sx={rowGridSx} mb={{ xs: 2, xl: 3.5 }}>
          <Typography sx={headerSx}>Event Name</Typography>
          <Typography textAlign='left' sx={headerSx}>Organisation</Typography>
          <Typography textAlign='left' sx={headerSx}>Requested By</Typography>
          <Typography textAlign='left' sx={headerSx}>Request Date</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: { xs: '10px', md: '13px', lg: '14px', xl: '20px' }, color: '#f59e0b' }} />
            <Typography textAlign='left' sx={headerSx}>Expected Delivery</Typography>
          </Box>
          <Typography textAlign='right' sx={headerSx}>Actions</Typography>
        </Box>

        <Box>
          {isLoading ? (
            <Box sx={{ width: '100%', mt: 15 }}>
              <Typography variant='h3' textAlign='center'>Loading...</Typography>
            </Box>
          ) : requests.length === 0 ? (
            <Typography textAlign='center' mt='5%' sx={{ fontFamily: 'Rubik', color: '#888' }}>
              No pending quick turn requests
            </Typography>
          ) : (
            requests.map((req) => {
              const loading = actionLoading === req.id;
              return (
                <React.Fragment key={req.id}>
                  {/* ── Desktop row ── */}
                  <Box
                    className={classes.tableRowData}
                    sx={{
                      ...rowGridSx,
                      height: { lg: '55px', xl: '65px' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <BoltIcon sx={{ fontSize: { xs: '12px', md: '14px', lg: '16px', xl: '22px' }, color: '#f59e0b', flexShrink: 0 }} />
                      <Typography sx={cellSx}>{req.event_name}</Typography>
                    </Box>

                    <Typography textAlign='left' sx={cellSx}>{req.organisation_name}</Typography>

                    <Typography textAlign='left' sx={cellSx}>{req.user_name}</Typography>

                    <Typography textAlign='left' sx={cellSx}>{formatDate(req.created_at)}</Typography>

                    <Typography textAlign='left' sx={{ ...cellSx, color: '#f59e0b', fontWeight: 600 }}>
                      {formatDelivery(req.expected_delivery)}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                      {loading ? (
                        <CircularProgress size={16} sx={{ color: '#666', mx: 1 }} />
                      ) : (
                        <>
                          <Button
                            variant='outlined'
                            size='small'
                            disabled={!!actionLoading}
                            onClick={() => onAccept && onAccept(req.id)}
                            startIcon={<CheckCircleOutlineIcon />}
                            style={{ borderRadius: '5px', backgroundColor: '#08231B', color: '#00d744' }}
                            sx={{ ...btnSx, borderColor: '#08231B', '&:hover': { borderColor: '#f1f6f2', color: '#00d744', backgroundColor: '#08231B80' } }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant='outlined'
                            size='small'
                            disabled={!!actionLoading}
                            onClick={() => onReject && onReject(req.id)}
                            startIcon={<CancelOutlinedIcon />}
                            style={{ borderRadius: '5px', color: '#d32f2f' }}
                            sx={{ ...btnSx, borderColor: '#08231B', '&:hover': { borderColor: '#d32f2f', color: '#d32f2f', backgroundColor: 'rgba(211,47,47,0.16)' } }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* ── Mobile / Tablet card ── */}
                  <Box
                    sx={{
                      width: '100%',
                      display: { xs: 'flex', md: 'none' },
                      flexDirection: 'column',
                      borderBottom: '1px solid #EBEBEB',
                      py: 1.5,
                      px: 1,
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BoltIcon sx={{ fontSize: '14px', color: '#f59e0b' }} />
                      <Typography sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: { xs: '12px', sm: '13px' }, color: '#08231B' }}>
                        {req.event_name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px' }, color: '#555' }}>
                      {req.organisation_name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px' }, color: '#888' }}>{req.user_name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {loading ? (
                          <CircularProgress size={14} sx={{ color: '#666' }} />
                        ) : (
                          <>
                            <Button
                              variant='outlined'
                              disabled={!!actionLoading}
                              onClick={() => onAccept && onAccept(req.id)}
                              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: '11px !important' }} />}
                              style={{ borderRadius: '5px', backgroundColor: '#08231B', color: '#00d744' }}
                              sx={mobileBtnSx}
                            >
                              Accept
                            </Button>
                            <Button
                              variant='outlined'
                              disabled={!!actionLoading}
                              onClick={() => onReject && onReject(req.id)}
                              startIcon={<CancelOutlinedIcon sx={{ fontSize: '11px !important' }} />}
                              style={{ borderRadius: '5px', color: '#d32f2f' }}
                              sx={{ ...mobileBtnSx, borderColor: '#08231B', '&:hover': { borderColor: '#d32f2f', color: '#d32f2f', backgroundColor: 'rgba(211,47,47,0.16)' } }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px' }, color: '#888' }}>
                      Request Date : {formatDate(req.created_at)}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px' }, color: '#f59e0b', fontWeight: 600 }}>
                      Deliver by: {formatDelivery(req.expected_delivery)}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </Box>

        {!isLoading && hasNextPage && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onLoadMore}
              sx={{ textTransform: 'none', fontFamily: 'Rubik', fontWeight: 600, borderRadius: '5px' }}
            >
              Load more
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QuickTurnTable;
